import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Gamepad2, Puzzle, ServerCog } from "lucide-react";
import Reveal from "@/components/Reveal";
import EmberCanvas from "@/components/EmberCanvas";
import { EMBLEM_URL, IMG } from "@/data/content";

const MANIFESTO = [
  {
    code: "01",
    title: "UNCOMPROMISING DEPTH",
    body: "World design where every border, law and battlefield has history. If it exists in the world, it has a reason to exist.",
  },
  {
    code: "02",
    title: "INDEPENDENT AUTONOMY",
    body: "100% creative control. No executive committees watering down the vision between the idea and your hands.",
  },
  {
    code: "03",
    title: "PROVEN IN PRODUCTION",
    body: "Every system we build is battle-tested on a live network before it ships anywhere else. Theory means nothing at full pop.",
  },
];

const STATS = [
  ["5", "SERVERS RUN DAILY"],
  ["8+", "SYSTEMS IN PRODUCTION"],
  ["2", "GAME ECOSYSTEMS SERVED"],
  ["40+", "PIONEER CITIZENS PLAYTESTING"],
];

const TIMELINE = [
  { date: "ORIGIN", label: "Arsenal network established", done: true },
  { date: "SCALE", label: "Plugin suite live across five Rust servers", done: true },
  { date: "BUILD", label: "Territoria enters active development", done: true },
  { date: "TODAY", label: "Pioneer server live — engine v0.0.007", done: true },
  { date: "01.01.2027", label: "Territoria open beta", done: false },
];

const About = () => (
  <div className="relative pt-32">
    <div
      className="pointer-events-none absolute inset-x-0 top-0 h-[520px]"
      style={{
        background: "radial-gradient(ellipse 60% 100% at 50% 0%, rgba(229,46,46,0.08) 0%, transparent 70%)",
      }}
    />
    <header className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
      <Reveal>
        <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">THE FORGE // THE STUDIO</p>
        <h1 className="mt-5 max-w-4xl font-display text-5xl font-black uppercase leading-[0.95] tracking-tight text-[#F4F5F7] sm:text-7xl lg:text-8xl">
          A studio with two hammers
        </h1>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mt-10 grid gap-10 lg:grid-cols-[auto_1fr] lg:items-center lg:gap-16">
          <img src={EMBLEM_URL} alt="Arsenal Forge emblem" className="emblem-glow w-24 sm:w-32" />
          <p className="max-w-2xl font-display text-2xl font-semibold uppercase leading-snug tracking-wide text-[#C6CAD2] sm:text-3xl">
            We build worlds — and we build the systems that keep them alive.
          </p>
        </div>
        <div className="mt-10 grid max-w-4xl gap-6 text-base leading-relaxed text-[#9EA4B0] sm:text-lg lg:grid-cols-2">
          <p>
            Arsenal Forge is the development arm of the Arsenal family. Born out of years running a
            live game server network, the studio exists to build what we always wanted to play —
            starting with Territoria, a persistent geopolitical MMO where players shape every border,
            law and war.
          </p>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#9EA4B0] sm:text-lg">
            The majority of the studio's craft today is production engineering for games: custom
            Oxide plugins for Rust, native addons for World of Warcraft, and the server admin
            applications and release pipelines that keep a five-server network running. That
            pressure is what forges quality.
          </p>
        </div>
      </Reveal>
    </header>

    <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
      <div className="grid grid-cols-2 gap-px border border-[#282D37] bg-[#282D37] lg:grid-cols-4">
        {STATS.map(([v, k], i) => (
          <Reveal key={k} delay={0.07 * i}>
            <div data-testid={`about-stat-${i}`} className="bg-[#0D0F12] px-6 py-10 text-center">
              <p className="font-display text-4xl font-black text-[#E52E2E] sm:text-5xl">{v}</p>
              <p className="mt-2 font-mono text-[10px] tracking-[0.3em] text-[#9EA4B0]">{k}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section data-testid="studio-disciplines" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
      <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">WHAT WE DO</p>
          <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
            Where the forge burns today
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[#9EA4B0]">
            Game development, plugin & addon engineering, and live server operations are what's
            through the door so far. The studio is just getting started — the forge is built to expand.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          <Reveal>
            <Link
              to="/games/territoria"
              data-testid="discipline-games"
              className="group relative block h-full overflow-hidden border border-[#282D37]"
            >
              <img
                src={IMG.mountainBiome}
                alt="Territoria"
                className="absolute inset-0 h-full w-full object-cover opacity-30 transition-[transform,opacity] duration-700 group-hover:scale-105 group-hover:opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/70 to-[#0A0B0D]/30" />
              <div className="relative p-8 sm:p-12">
                <Gamepad2 size={28} className="text-[#E52E2E]" />
                <p className="mt-6 font-mono text-[10px] tracking-[0.3em] text-[#E52E2E]">DISCIPLINE 01 // GAME DEVELOPMENT</p>
                <h3 className="mt-3 font-display text-3xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-4xl">
                  Territoria
                </h3>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-[#C6CAD2] sm:text-base">
                  A persistent multiplayer political, economic and military simulator. 137 nations,
                  685 regions, one world, no resets. Built on our own engine, tested in the open on a
                  live pioneer server, open beta locked for 01.01.2027.
                </p>
                <span className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] font-bold tracking-[0.25em] text-[#F4F5F7] transition-colors duration-300 group-hover:text-[#E52E2E]">
                  VIEW THE PROJECT
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
          <Reveal delay={0.12}>
            <Link
              to="/plugins"
              data-testid="discipline-plugins"
              className="group relative block h-full overflow-hidden border border-[#282D37]"
            >
              <img
                src={IMG.welderCraft}
                alt="Plugin engineering"
                className="absolute inset-0 h-full w-full object-cover opacity-30 transition-[transform,opacity] duration-700 group-hover:scale-105 group-hover:opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/70 to-[#0A0B0D]/30" />
              <div className="relative p-8 sm:p-10">
                <Puzzle size={28} className="text-[#E52E2E]" />
                <p className="mt-6 font-mono text-[10px] tracking-[0.3em] text-[#E52E2E]">DISCIPLINE 02 // PLUGIN & ADDON ENGINEERING</p>
                <h3 className="mt-3 font-display text-3xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-4xl">
                  The Foundry
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-[#C6CAD2] sm:text-base">
                  Custom Oxide plugins for Rust and native World of Warcraft addons — statistics,
                  identity, clans, kits and interfaces, all proven live on a running network.
                </p>
                <span className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] font-bold tracking-[0.25em] text-[#F4F5F7] transition-colors duration-300 group-hover:text-[#E52E2E]">
                  ENTER THE FOUNDRY
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
          <Reveal delay={0.24}>
            <Link
              to="/plugins"
              data-testid="discipline-ops"
              className="group relative block h-full overflow-hidden border border-[#282D37]"
            >
              <img
                src={IMG.forgeSparks}
                alt="Server operations tooling"
                className="absolute inset-0 h-full w-full object-cover opacity-30 transition-[transform,opacity] duration-700 group-hover:scale-105 group-hover:opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/70 to-[#0A0B0D]/30" />
              <div className="relative p-8 sm:p-10">
                <ServerCog size={28} className="text-[#E52E2E]" />
                <p className="mt-6 font-mono text-[10px] tracking-[0.3em] text-[#E52E2E]">DISCIPLINE 03 // SERVER OPS & ADMIN APPS</p>
                <h3 className="mt-3 font-display text-3xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-4xl">
                  Live Operations
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-[#C6CAD2] sm:text-base">
                  Server administration apps, telemetry bridges and a Gold Master release pipeline —
                  the operational machinery behind a five-server community.
                </p>
                <span className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] font-bold tracking-[0.25em] text-[#F4F5F7] transition-colors duration-300 group-hover:text-[#E52E2E]">
                  SEE THE PIPELINE
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
        </div>
      </div>
    </section>

    <section data-testid="craft-manifesto" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
      <Reveal>
        <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">CRAFT MANIFESTO</p>
        <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
          What the forge stands on
        </h2>
      </Reveal>
      <div className="mt-14 space-y-px">
        {MANIFESTO.map((m, i) => (
          <Reveal key={m.code} delay={0.08 * i}>
            <div
              data-testid={`manifesto-${m.code}`}
              className="group grid gap-4 border border-[#282D37] bg-[#121418] p-8 transition-[border-color,background-color] duration-500 hover:border-[#E52E2E]/50 sm:p-10 lg:grid-cols-[120px_1fr_1.4fr] lg:items-center lg:gap-12"
            >
              <span className="font-display text-5xl font-black text-[#282D37] transition-colors duration-500 group-hover:text-[#E52E2E]">
                {m.code}
              </span>
              <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-[#F4F5F7] sm:text-3xl">
                {m.title}
              </h3>
              <p className="text-sm leading-relaxed text-[#9EA4B0] sm:text-base">{m.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section data-testid="studio-timeline" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
      <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">THE ROAD SO FAR</p>
          <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
            From servers to worlds
          </h2>
        </Reveal>
        <div className="mt-14">
          {TIMELINE.map((m, i) => (
            <Reveal key={m.label} delay={0.08 * i}>
              <div data-testid={`about-timeline-${i}`} className="relative flex gap-8 border-l border-[#282D37] pb-12 pl-8 last:pb-0">
                <span
                  className={`absolute -left-[7px] top-1 h-3.5 w-3.5 rotate-45 border ${
                    m.done ? "border-[#E52E2E] bg-[#E52E2E]" : "border-[#3A404E] bg-[#0A0B0D]"
                  }`}
                />
                <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-6">
                  <span className="w-32 shrink-0 font-mono text-xs font-bold tracking-[0.25em] text-[#E52E2E]">{m.date}</span>
                  <span className={`font-display text-xl font-bold uppercase tracking-wide sm:text-2xl ${m.done ? "text-[#F4F5F7]" : "text-[#5F6573]"}`}>
                    {m.label}
                  </span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="studio-environment" className="relative mx-auto max-w-[1400px] overflow-hidden px-6 py-24 lg:px-10">
      <EmberCanvas density={12} className="pointer-events-none absolute inset-0 h-full w-full opacity-40" />
      <Reveal>
        <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">STUDIO ENVIRONMENT</p>
        <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
          Inside the stronghold
        </h2>
      </Reveal>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {[
          { src: IMG.studioSetup, caption: "CORE DEVELOPMENT BAY" },
          { src: IMG.studioWork, caption: "SYSTEMS & WORLDCRAFT" },
          { src: IMG.welderCraft, caption: "MATERIAL RESEARCH" },
        ].map((img, i) => (
          <Reveal key={img.caption} delay={0.1 * i} className={i === 1 ? "md:mt-12" : ""}>
            <figure className="group border border-[#282D37]">
              <div className="overflow-hidden">
                <img
                  src={img.src}
                  alt={img.caption}
                  className="aspect-[4/5] w-full object-cover opacity-80 transition-[transform,opacity] duration-700 group-hover:scale-105 group-hover:opacity-100"
                />
              </div>
              <figcaption className="bg-[#121418] px-5 py-4 font-mono text-[10px] tracking-[0.3em] text-[#9EA4B0]">
                {img.caption}
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-20" data-testid="arsenal-family">
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">THE ARSENAL FAMILY</p>
          <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
            One arsenal. Two fronts.
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <div className="steel-panel p-8 sm:p-10">
              <img src={EMBLEM_URL} alt="Arsenal Forge emblem" className="w-14" />
              <h3 className="mt-6 font-display text-2xl font-bold uppercase tracking-wide text-[#F4F5F7]">
                Arsenal Forge
              </h3>
              <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-[#E52E2E]">THE STUDIO</p>
              <p className="mt-4 text-sm leading-relaxed text-[#9EA4B0]">
                The development arm — forging Territoria and the proprietary plugin systems that run
                the network. Worlds and the machinery behind them.
              </p>
            </div>
            <a
              href="https://arsenalservers.co"
              target="_blank"
              rel="noreferrer"
              data-testid="arsenal-servers-card"
              className="group steel-panel relative block p-8 transition-[border-color,box-shadow] duration-500 hover:border-[#E52E2E]/50 hover:shadow-[0_0_40px_rgba(229,46,46,0.08)] sm:p-10"
            >
              <img
                src="https://arsenalservers.co/arsenal-emblem.png"
                alt="Arsenal Servers emblem"
                className="w-14 transition-transform duration-500 group-hover:scale-105"
              />
              <h3 className="mt-6 font-display text-2xl font-bold uppercase tracking-wide text-[#F4F5F7] transition-colors duration-300 group-hover:text-[#E52E2E]">
                Arsenal Servers
              </h3>
              <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-[#E52E2E]">THE NETWORK</p>
              <p className="mt-4 text-sm leading-relaxed text-[#9EA4B0]">
                The premium game server network — performance, progression and competitive play,
                starting with Rust. Your game. Your stats. Your legacy.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] font-bold tracking-[0.25em] text-[#9EA4B0] transition-colors duration-300 group-hover:text-[#E52E2E]">
                VISIT ARSENALSERVERS.CO
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
              <span className="absolute bottom-0 left-0 h-px w-0 bg-[#E52E2E] transition-[width] duration-700 group-hover:w-full" />
            </a>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.2}>
        <div className="mt-20 border border-[#282D37] bg-[#0D0F12] p-10 text-center sm:p-16">
          <p className="font-display text-3xl font-black uppercase leading-tight tracking-tight text-[#F4F5F7] sm:text-5xl">
            Small team. <span className="text-[#E52E2E]">Big worlds.</span>
          </p>
          <Link
            to="/games/territoria"
            data-testid="about-cta-territoria"
            className="mt-8 inline-block bg-[#E52E2E] px-9 py-4 font-mono text-sm font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-[#FF3B3B]"
          >
            See what we're forging
          </Link>
        </div>
      </Reveal>
    </section>
  </div>
);

export default About;
